// ============================================================
// ActionDrum - the chamber selector behind the ACTION button (and a
// right-click on the trigger).
//
// The armoury drum's grammar on a smaller cylinder: plates revolve
// around a fixed cursor row, the wheel turns one action per notch,
// Arrow keys browse with wrap, Enter CHAMBERS the focused plate, Esc
// closes. Clicking ANY enabled plate chambers it in ONE click
// (v8.238.0 - the old click-to-centre-then-click-again two-step had
// people clicking through the closed drum into the page behind).
// Nothing fires from here - since v8.236.0 the drum holds only
// decisive actions and selecting one loads the trigger, which is
// what fires.
//
// Plates beyond four notches fade out entirely, which is also what
// makes the wrap seam invisible - the plate that teleports across the
// cylinder does so at opacity 0. Home and End jump to the first and
// last chamberable plate, because a long cylinder is a long way to
// arrow through.
//
// Runs of the same `section` are captioned from the catalog's own
// order (sectionCaptions). Indices, navigation, disabled-skip and
// aria-activedescendant are untouched by that: a caption is a label a
// plate wears, never a row of its own.
// ============================================================
import { useState, useRef, useEffect, useCallback, type KeyboardEvent, type WheelEvent } from 'react';
import type { ActionDef } from '../../engine/actions/types';
import { sectionCaptions } from '../../engine/actions/types';
import { NOTCH_STEPS, type Notch } from '../../engine/actions/difficultyNotch';
import ActionPlate from './ActionPlate';
import { setSquadPreview } from '../../hooks/useSquadPreview';
import { circularOffset, endIndex, stepCursor, wheelScale } from './drumNavigation';
import styles from '../../styles/ActionDrum.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  defs: ActionDef[];
  /** The action currently loaded in the trigger, if any. */
  chamberedId: string | null;
  /** id -> why it cannot chamber; absent = chamberable. */
  disabledReasons: Record<string, string>;
  /** Actions whose state is currently held - an engaged stance. */
  engagedIds?: string[];
  onExecute: (id: string) => void;
  /** The difficulty notch (engine/actions/difficultyNotch); absent, the drum draws none. */
  notch?: NotchProps;
}

interface NotchProps {
  value: Notch;
  /** The other side's difficulty is the director's alone. */
  canSetTheirs: boolean;
  onTurn: (side: keyof Notch, mod: number) => void;
}

const WHEEL_THRESHOLD = 60;

/** One side of the notch, as a select over the ladder. A value off the ladder is offered as itself. */
function NotchSelect({
  label,
  testId,
  value,
  onChange,
}: {
  label: string;
  testId: string;
  value: number;
  onChange: (mod: number) => void;
}) {
  const offLadder = !NOTCH_STEPS.some((step) => step.mod === value);
  return (
    <label className={styles.notchField}>
      <span className={styles.notchCaption}>{label}</span>
      <select
        className={styles.notchSelect}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {offLadder && <option value={value}>{value >= 0 ? `+${value}` : value}</option>}
        {NOTCH_STEPS.map((step) => (
          <option key={step.mod} value={step.mod}>
            {step.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The notch above the cylinder: how hard the next test is. It applies to
 * the next action that IS a test, then returns to Challenging.
 */
function DifficultyNotch({ notch }: { notch: NotchProps }) {
  return (
    // Its own keys and clicks stay its own: the drum closes on a click outside it, not on one here.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- a click guard, not a control
    <div className={styles.notch} data-testid="drum-notch" onClick={(e) => e.stopPropagation()}>
      <NotchSelect
        label="Difficulty of the next test"
        testId="drum-notch-mine"
        value={notch.value.mine}
        onChange={(mod) => notch.onTurn('mine', mod)}
      />
      {notch.canSetTheirs && (
        <NotchSelect
          label="The other side"
          testId="drum-notch-theirs"
          value={notch.value.theirs}
          onChange={(mod) => notch.onTurn('theirs', mod)}
        />
      )}
    </div>
  );
}

export default function ActionDrum({
  open,
  onClose,
  defs,
  chamberedId,
  disabledReasons,
  engagedIds = [],
  onExecute,
  notch,
}: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { cursor, setCursor, navigate, chamberable } = useDrumCursor(open, defs, disabledReasons);
  const onWheel = useDrumWheel(navigate);
  const captions = sectionCaptions(defs);

  useEffect(() => {
    if (open) rootRef.current?.focus();
  }, [open]);
  usePreviewAt(open ? defs[cursor] : undefined);

  if (!open || defs.length === 0) return null;

  const onKeyDown = drumKeyHandler({ defs, cursor, setCursor, navigate, chamberable, onExecute, onClose });

  return (
    <div className={styles.overlay} data-testid="action-drum" onClick={onClose} role="presentation">
      {notch && <DifficultyNotch notch={notch} />}
      <div
        ref={rootRef}
        className={styles.drum}
        role="listbox"
        aria-label="Combat actions"
        aria-activedescendant={`action-plate-${defs[cursor]?.id}`}
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
        onWheel={onWheel}
        onKeyDown={onKeyDown}
      >
        {defs.map((def, i) => (
          <ActionPlate
            key={def.id}
            def={def}
            caption={captions[def.id]}
            offset={circularOffset(i, cursor, defs.length)}
            chambered={def.id === (chamberedId ?? 'standardAttack')}
            engaged={engagedIds.includes(def.id)}
            disabledReason={disabledReasons[def.id]}
            focused={i === cursor}
            onExecute={onExecute}
          />
        ))}
        <div className={styles.cursorRail} aria-hidden="true" />
      </div>
    </div>
  );
}

interface DrumKeys {
  defs: ActionDef[];
  cursor: number;
  setCursor: (i: number) => void;
  navigate: (delta: number) => void;
  chamberable: (i: number) => boolean;
  onExecute: (id: string) => void;
  onClose: () => void;
}

/**
 * The drum's keys: arrows browse, Enter chambers, Home/End jump, Escape
 * closes. Handled keys never escape the drum: the window-level shortcuts
 * (Space/Enter attack, F toggles) must not fire underneath it.
 */
function drumKeyHandler({ defs, cursor, setCursor, navigate, chamberable, onExecute, onClose }: DrumKeys) {
  const jumpTo = (end: 'Home' | 'End') => {
    const found = endIndex(end, defs.length, chamberable);
    if (found !== undefined) setCursor(found);
  };
  const keys: Record<string, () => void> = {
    ArrowDown: () => navigate(1),
    ArrowUp: () => navigate(-1),
    Enter: () => {
      if (chamberable(cursor)) onExecute(defs[cursor].id);
    },
    ' ': () => undefined, // Space browses nothing and attacks nobody while the drum is up
    Home: () => jumpTo('Home'),
    End: () => jumpTo('End'),
    Escape: onClose,
  };
  return (e: KeyboardEvent) => {
    const handle = keys[e.key];
    if (!handle) return;
    e.preventDefault();
    e.stopPropagation();
    handle();
  };
}

/** The cursor resting on Enter or Challenge previews the brother's support ring on the map (Squad Mode, 2026-10-24). */
function usePreviewAt(def: ActionDef | undefined) {
  const verb = def?.killTeam?.verb;
  const previewing = verb === 'enter' || verb === 'challenge';
  useEffect(() => {
    setSquadPreview('drum', previewing);
    return () => setSquadPreview('drum', false);
  }, [previewing]);
}

/**
 * The drum's cursor. THE CURSOR NEVER RESTS ON A DISABLED PLATE
 * (v8.238.1): with a ranged weapon, Feint and Stun are dimmed - a cursor
 * stopping there put the highlight rectangle on something unselectable
 * while its neighbours worked, which reads as a broken drum. Disabled
 * plates stay visible in the cylinder (they still say WHY); the cursor
 * just steps over them. If the plate under the cursor becomes disabled
 * mid-open (a weapon swap), snap to the first chamberable one.
 */
function useDrumCursor(open: boolean, defs: ActionDef[], disabledReasons: Record<string, string>) {
  const [cursor, setCursor] = useState(0);
  const chamberable = useCallback((i: number) => !!defs[i] && !disabledReasons[defs[i].id], [defs, disabledReasons]);
  useEffect(() => {
    if (!open || chamberable(cursor)) return;
    const first = defs.findIndex((d) => !disabledReasons[d.id]);
    if (first >= 0) setCursor(first);
  }, [open, cursor, chamberable, defs, disabledReasons]);
  const navigate = (delta: number) => setCursor((c) => stepCursor(c, delta, defs.length, chamberable));
  return { cursor, setCursor, navigate, chamberable };
}

/**
 * The wheel: one action per notch, however the device slices its deltas.
 * ONE step per threshold crossing, then the accumulator clears. A discrete
 * mouse notch is ~120 delta units against a threshold of 60, so the old
 * carry-the-remainder loop stepped TWICE per notch and the drum skipped
 * every other action (v8.238.0). A partial scroll is forgotten after a
 * 250ms pause.
 */
function useDrumWheel(navigate: (delta: number) => void) {
  const wheelAcc = useRef(0);
  const wheelReset = useRef<ReturnType<typeof setTimeout> | null>(null);
  return (e: WheelEvent) => {
    wheelAcc.current += e.deltaY * wheelScale(e.deltaMode);
    if (wheelReset.current) clearTimeout(wheelReset.current);
    wheelReset.current = setTimeout(() => {
      wheelAcc.current = 0;
    }, 250);
    if (Math.abs(wheelAcc.current) < WHEEL_THRESHOLD) return;
    navigate(Math.sign(wheelAcc.current));
    wheelAcc.current = 0;
  };
}
